import { createReducer } from 'typesafe-actions';

import {
  SupportUserActionTypes,
  TSupportUserAction,
  ISupportUserState,
} from '@/features/dashboard-users/store/types/support-user.types';
import { Auth2FaActionTypes } from '@/features/2fa/store/types/auth2Fa.types';

const initialState: ISupportUserState = {
  dashboardUsers: { cursor: {}, elements: [] },
  create: {
    isLoading: false,
  },
  search: {
    isLoading: false,
  },
};

export const supportUserReducer = createReducer<ISupportUserState, TSupportUserAction>(initialState)
  .handleType(SupportUserActionTypes.SEARCH_SUPPORT_USERS, (state) => ({
    ...state,
    search: {
      error: undefined,
      isLoading: true,
    },
  }))

  .handleType(SupportUserActionTypes.SEARCH_SUPPORT_USERS_SUCCESS, (state, action) => {
    return {
      ...state,
      dashboardUsers: {
        elements: action.supportUsers.elements.map((supportUser) => ({
          state: supportUser,
        })),
        cursor: action.supportUsers.cursor,
      },
      search: {
        error: undefined,
        isLoading: false,
      },
    };
  })

  .handleType(SupportUserActionTypes.SEARCH_SUPPORT_USERS_FAIL, (state, action) => ({
    ...state,
    search: {
      isLoading: false,
      error: action.error,
    },
  }))
  .handleType(SupportUserActionTypes.SEARCH_SUSPENDED_SUPPORT_USERS, (state) => ({
    ...state,
    searchSuspendedDashboardUsers: {
      error: undefined,
      isLoading: true,
    },
  }))

  .handleType(SupportUserActionTypes.SEARCH_SUSPENDED_SUPPORT_USERS_SUCCESS, (state, action) => {
    return {
      ...state,
      suspendedDashboardUsers: {
        elements: action.supportUsers.elements.map((supportUser) => ({
          state: supportUser,
        })),
        cursor: action.supportUsers.cursor,
      },
      searchSuspendedDashboardUsers: {
        error: undefined,
        isLoading: false,
      },
    };
  })

  .handleType(SupportUserActionTypes.SEARCH_SUSPENDED_SUPPORT_USERS_FAIL, (state, action) => ({
    ...state,
    searchSuspendedDashboardUsers: {
      isLoading: false,
      error: action.error,
    },
  }))

  .handleType(SupportUserActionTypes.ADD_SUPPORT_USER, (state) => ({
    ...state,
    create: {
      isLoading: true,
      error: undefined,
    },
  }))

  .handleType(SupportUserActionTypes.ADD_SUPPORT_USER_SUCCESS, (state, action) => ({
    ...state,
    dashboardUsers: {
      ...state.dashboardUsers,
      elements: [...state.dashboardUsers.elements, { state: action.supportUser }],
    },
    create: {
      isLoading: false,
      error: undefined,
    },
  }))

  .handleType(SupportUserActionTypes.ADD_SUPPORT_USER_FAIL, (state, action) => ({
    ...state,
    create: {
      isLoading: false,
      error: action.error,
    },
  }))

  .handleType(SupportUserActionTypes.UPDATE_SUPPORT_USER, (state, action) => ({
    ...state,
    dashboardUsers: {
      ...state.dashboardUsers,
      elements: state.dashboardUsers.elements.map((dashboardUser) =>
        dashboardUser.state?.id === action.id
          ? {
              ...dashboardUser,
              isLoading: true,
            }
          : dashboardUser,
      ),
    },
  }))

  .handleType(SupportUserActionTypes.UPDATE_SUPPORT_USER_SUCCESS, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.supportUser.id
        ? {
            ...dashboardUser,
            state: action.supportUser,
            isLoading: false,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(SupportUserActionTypes.UPDATE_SUPPORT_USER_FAIL, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.id
        ? {
            ...dashboardUser,
            isLoading: false,
            error: action.error,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(SupportUserActionTypes.REMOVE_SUPPORT_USER, (state) => ({
    ...state,
    remove: {
      isLoading: true,
      error: undefined,
    },
  }))

  .handleType(SupportUserActionTypes.REMOVE_SUPPORT_USER_SUCCESS, (state, action) => ({
    ...state,
    ...(action.isSuspended
      ? {
          suspendedDashboardUsers: {
            ...state.suspendedDashboardUsers,
            elements: state.suspendedDashboardUsers?.elements?.filter(
              ({ state: dashboardUser }) => dashboardUser!.id !== action.supportUserId,
            ),
          },
        }
      : {
          dashboardUsers: {
            ...state.dashboardUsers,
            elements: state.dashboardUsers.elements.filter(
              ({ state: dashboardUser }) => dashboardUser!.id !== action.supportUserId,
            ),
          },
        }),
    remove: {
      isLoading: false,
      error: undefined,
    },
  }))

  .handleType(SupportUserActionTypes.REMOVE_SUPPORT_USER_FAIL, (state, action) => ({
    ...state,
    remove: {
      isLoading: false,
      error: action.error,
    },
  }))

  .handleType(SupportUserActionTypes.UNBLOCK_SUPPORT_USER, (state, action) => ({
    ...state,
    dashboardUsers: {
      ...state.dashboardUsers,
      elements: state.dashboardUsers.elements.map((dashboardUser) =>
        dashboardUser.state?.id === action.id
          ? {
              ...dashboardUser,
              isLoading: true,
            }
          : dashboardUser,
      ),
    },
  }))

  .handleType(SupportUserActionTypes.UNBLOCK_SUPPORT_USER_SUCCESS, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.supportUser.id
        ? {
            ...dashboardUser,
            state: action.supportUser,
            isLoading: false,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(SupportUserActionTypes.UNBLOCK_SUPPORT_USER_FAIL, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.id
        ? {
            ...dashboardUser,
            isLoading: false,
            error: action.error,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(SupportUserActionTypes.SUSPEND_SUPPORT_USER, (state, action) => ({
    ...state,
    dashboardUsers: {
      ...state.dashboardUsers,
      elements: state.dashboardUsers.elements.map((dashboardUser) =>
        dashboardUser.state?.id === action.id
          ? {
              ...dashboardUser,
              isLoading: true,
            }
          : dashboardUser,
      ),
    },
  }))

  .handleType(SupportUserActionTypes.SUSPEND_SUPPORT_USER_SUCCESS, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.filter(
      (dashboardUser) => dashboardUser.state?.id !== action.supportUser.id,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(SupportUserActionTypes.SUSPEND_SUPPORT_USER_FAIL, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.id
        ? {
            ...dashboardUser,
            isLoading: false,
            error: action.error,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })
  .handleType(SupportUserActionTypes.RESTORE_SUPPORT_USER_ACCESS, (state, action) => ({
    ...state,
    suspendedDashboardUsers: {
      ...state.suspendedDashboardUsers,
      elements: state.suspendedDashboardUsers.elements.map((dashboardUser) =>
        dashboardUser.state?.id === action.id
          ? {
              ...dashboardUser,
              isLoading: true,
            }
          : dashboardUser,
      ),
    },
  }))

  .handleType(SupportUserActionTypes.RESTORE_SUPPORT_USER_ACCESS_SUCCESS, (state, action) => {
    const updatedDashboardUsersState = state.suspendedDashboardUsers.elements.filter(
      (dashboardUser) => dashboardUser.state?.id !== action.supportUser.id,
    );

    return {
      ...state,
      suspendedDashboardUsers: { ...state.suspendedDashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(SupportUserActionTypes.RESTORE_SUPPORT_USER_ACCESS_FAIL, (state, action) => {
    const updatedDashboardUsersState = state.suspendedDashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.id
        ? {
            ...dashboardUser,
            isLoading: false,
            error: action.error,
          }
        : dashboardUser,
    );

    return {
      ...state,
      suspendedDashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })
  .handleType(SupportUserActionTypes.SET_REVIEW, (state) => {
    return {
      ...state,
      dashboardUsers: {
        ...state.dashboardUsers,
        isLoading: true,
      },
    };
  })
  .handleType(SupportUserActionTypes.SET_REVIEW_FAIL, (state) => {
    return {
      ...state,
      dashboardUsers: {
        ...state.dashboardUsers,
        isLoading: false,
      },
    };
  })
  .handleType(SupportUserActionTypes.SET_REVIEW_SUCCESS, (state) => {
    return {
      ...state,
      dashboardUsers: {
        ...state.dashboardUsers,
        isLoading: false,
      },
    };
  })

  .handleType(Auth2FaActionTypes.DELETE_PASSKEY, (state, action) => ({
    ...state,
    dashboardUsers: {
      ...state.dashboardUsers,
      elements: state.dashboardUsers.elements.map((dashboardUser) =>
        dashboardUser.state?.id === action.dashboardUserId
          ? {
              ...dashboardUser,
              isLoading: true,
            }
          : dashboardUser,
      ),
    },
  }))

  .handleType(Auth2FaActionTypes.DELETE_PASSKEY_SUCCESS, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.dashboardUserId
        ? {
            ...dashboardUser,
            state: { ...dashboardUser.state, secureKey: null },
            isLoading: false,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  })

  .handleType(Auth2FaActionTypes.DELETE_PASSKEY_FAIL, (state, action) => {
    const updatedDashboardUsersState = state.dashboardUsers.elements.map((dashboardUser) =>
      dashboardUser.state?.id === action.dashboardUserId
        ? {
            ...dashboardUser,
            isLoading: false,
            error: action.error,
          }
        : dashboardUser,
    );

    return {
      ...state,
      dashboardUsers: { ...state.dashboardUsers, elements: updatedDashboardUsersState },
    };
  });
